/**
 * Planetas.ts — Render dos planetas (Fase 6: corpo com relevo em textura,
 * aura de territorio na cor do dono, aro atmosferico, numero de naves,
 * torres e tipo especial).
 *
 * O sprite e um Container com varios filhos:
 *   - aura (Sprite radial tintado — o "territorio", grande no planeta com dono)
 *   - anel orbital (linha fina onde as naves giram)
 *   - aro especial (se industrial/fortaleza/trampolim)
 *   - corpo (Sprite com a textura gerada: esfera sombreada, crateras/faixas)
 *   - aro do dono (contorno na cor + linha atmosferica externa)
 *   - texto com numero de naves
 *   - icone + label do tipo especial
 *   - graficos de torres (preenchidas/vazias) ao redor
 *
 * Sem GlowFilter: a aura + o bloom global fazem o brilho, mais barato.
 */

import { Container, Graphics, Sprite, Text } from 'pixi.js';
import {
  corPlaneta,
  CORES_ESPECIAIS,
  EMOJIS_ESPECIAIS,
  NOMES_ESPECIAIS,
} from '../config';
import type { Planeta, EstadoJogo } from '../domain/tipos';
import type { PixiApp } from './PixiApp';
import { texturaAura, texturaPlaneta } from './Texturas';

export class Planetas {
  constructor(
    private readonly pixi: PixiApp,
    private readonly estado: EstadoJogo,
  ) {}

  /** Cria sprites de todos os planetas. Chama ao receber partida_inicio. */
  renderTodos(): void {
    this.pixi.planetasLayer.removeChildren();
    for (const p of this.estado.planetas.values()) {
      this.criarERegistrar(p);
    }
  }

  /** Cria (ou recria) o sprite de um planeta especifico. */
  criarERegistrar(p: Planeta): void {
    const cor = corPlaneta(p.donoId);
    const sprite = criarSpritePlaneta(p, cor, this.raioOrbitalExtra());
    sprite.x = p.x;
    sprite.y = p.y;
    if (p.spriteContainer) {
      this.pixi.planetasLayer.removeChild(p.spriteContainer);
      p.spriteContainer.destroy({ children: true });
    }
    p.spriteContainer = sprite;
    this.pixi.planetasLayer.addChild(sprite);
  }

  /** Atualiza so o texto do numero de naves (mais barato que recriar tudo). */
  atualizarTextoNaves(p: Planeta): void {
    if (!p.spriteContainer) return;
    const t = p.spriteContainer.getChildByLabel('naves');
    if (t instanceof Text) t.text = String(p.naves || 0);
  }

  private raioOrbitalExtra(): number {
    return (this.estado.config?.nave_raio_orbital_extra as number) ?? 15;
  }
}

// ---------------------------------------------------------------------------
// Construcao do sprite (funcao pura, sem estado)
// ---------------------------------------------------------------------------

function criarSpritePlaneta(p: Planeta, cor: number, raioOrbitalExtra: number): Container {
  const c = new Container();
  const temDono = p.donoId > 0;
  const raioOrbital = p.raio + raioOrbitalExtra;

  // Aura de territorio (grande e viva com dono; discreta no neutro)
  const aura = new Sprite(texturaAura());
  aura.anchor.set(0.5);
  const tamAura = temDono ? p.raio * 6.2 : p.raio * 3.4;
  aura.width = tamAura;
  aura.height = tamAura;
  aura.tint = cor;
  aura.alpha = temDono ? 0.30 : 0.14;
  aura.blendMode = 'add';
  c.addChild(aura);

  // Anel orbital: onde as naves giram
  const anel = new Graphics();
  anel.circle(0, 0, raioOrbital).stroke({ color: cor, width: 1, alpha: temDono ? 0.16 : 0.10 });
  c.addChild(anel);

  // Aro colorido se planeta especial
  if (p.especial && CORES_ESPECIAIS[p.especial]) {
    const aroEspecial = new Graphics();
    aroEspecial
      .circle(0, 0, p.raio + 7)
      .stroke({ color: CORES_ESPECIAIS[p.especial], width: 2.5, alpha: 0.85 });
    aroEspecial
      .circle(0, 0, p.raio + 11)
      .stroke({ color: CORES_ESPECIAIS[p.especial], width: 1, alpha: 0.35 });
    c.addChild(aroEspecial);
  }

  // Corpo com relevo
  const corpo = new Sprite(texturaPlaneta(p.id, p.raio));
  corpo.anchor.set(0.5);
  corpo.width = p.raio * 2;
  corpo.height = p.raio * 2;
  c.addChild(corpo);

  // Aro do dono: contorno + linha atmosferica
  const aro = new Graphics();
  aro.circle(0, 0, p.raio).stroke({ color: cor, width: temDono ? 2.5 : 1.5, alpha: temDono ? 0.95 : 0.5 });
  if (temDono) {
    aro.circle(0, 0, p.raio + 3.5).stroke({ color: cor, width: 1.2, alpha: 0.35 });
  }
  c.addChild(aro);

  // Numero de naves no centro
  const naveText = new Text({
    text: String(p.naves || 0),
    style: {
      fill: 0xffffff,
      fontSize: Math.max(14, p.raio * 0.55),
      fontWeight: '700',
      fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
      align: 'center',
      stroke: { color: 0x000000, width: 3 },
      dropShadow: { color: 0x000000, alpha: 0.6, blur: 4, distance: 0 },
    },
  });
  naveText.anchor.set(0.5);
  naveText.alpha = 0.97;
  naveText.label = 'naves';
  c.addChild(naveText);

  // Icone do tipo especial (abaixo do numero de naves)
  if (p.especial && EMOJIS_ESPECIAIS[p.especial]) {
    const iconeEsp = new Text({
      text: EMOJIS_ESPECIAIS[p.especial],
      style: {
        fill: CORES_ESPECIAIS[p.especial],
        fontSize: Math.max(13, p.raio * 0.38),
        fontWeight: '600',
        fontFamily: 'system-ui, -apple-system, sans-serif',
      },
    });
    iconeEsp.anchor.set(0.5);
    iconeEsp.y = p.raio * 0.42;
    iconeEsp.alpha = 0.95;
    c.addChild(iconeEsp);

    // Label flutuante acima do anel de torres
    const distTorre = Math.max(14, Math.round(p.raio * 0.28));
    const distLabelTopo = raioOrbital + distTorre + Math.max(22, p.raio * 0.38);
    const labelEsp = new Text({
      text: NOMES_ESPECIAIS[p.especial] || p.especial,
      style: {
        fill: CORES_ESPECIAIS[p.especial],
        fontSize: Math.max(9, p.raio * 0.20),
        fontWeight: '700',
        fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
        letterSpacing: 1.2,
      },
    });
    labelEsp.anchor.set(0.5);
    labelEsp.y = -distLabelTopo;
    labelEsp.alpha = 0.85;
    c.addChild(labelEsp);
  }

  // Indicadores de torres (preenchidas / slots vazios)
  const mostrarSlots = p.slotsTorre > 0 && (p.donoId !== 0 || p.torres > 0);
  if (mostrarSlots) {
    const torreH = Math.max(14, Math.round(p.raio * 0.42));
    const torreW = Math.max(9, Math.round(p.raio * 0.30));
    const torreR = raioOrbital + Math.max(14, Math.round(p.raio * 0.28));

    for (let t = 0; t < p.slotsTorre; t++) {
      const ang = (t / p.slotsTorre) * Math.PI * 2 - Math.PI / 2;
      const tx = Math.cos(ang) * torreR;
      const ty = Math.sin(ang) * torreR;
      const preenchido = t < p.torres;

      const g = new Graphics();
      if (preenchido) {
        desenharTorrePreenchida(g, cor, torreW, torreH);
      } else {
        desenharSlotVazio(g, cor, torreW);
      }

      g.rotation = ang + Math.PI / 2;
      g.x = tx;
      g.y = ty;
      c.addChild(g);
    }
  }

  return c;
}

function desenharTorrePreenchida(g: Graphics, cor: number, torreW: number, torreH: number): void {
  // Sombra/base
  const halfBaseShadow = torreW * 0.7;
  g.ellipse(0, torreH * 0.4, halfBaseShadow, torreW * 0.18)
    .fill({ color: 0x000000, alpha: 0.55 });

  // Base trapezoidal
  const halfBase = torreW * 0.55;
  const halfTop = torreW * 0.30;
  const baseY = torreH * 0.32;
  const topY = -torreH * 0.20;
  const poly = [
    -halfBase, baseY,
     halfBase, baseY,
     halfTop,  topY,
    -halfTop,  topY,
  ];
  g.poly(poly).fill({ color: cor, alpha: 1.0 });
  g.poly(poly).stroke({ color: 0xffffff, width: 1.5, alpha: 0.6 });

  // Faixa horizontal media
  g.rect(-halfBase * 0.85, baseY * 0.05, halfBase * 1.7, torreH * 0.08)
    .fill({ color: 0xffffff, alpha: 0.25 });

  // Hastinha do canhao
  const hasteW = torreW * 0.22;
  const hasteH = torreH * 0.38;
  g.rect(-hasteW / 2, topY - hasteH, hasteW, hasteH).fill({ color: cor, alpha: 1.0 });
  g.rect(-hasteW / 2, topY - hasteH, hasteW, hasteH)
    .stroke({ color: 0xffffff, width: 1, alpha: 0.5 });

  // Bolinha brilhante (canhao)
  const bolaY = topY - hasteH - torreW * 0.22;
  const bolaR = torreW * 0.34;
  g.circle(0, bolaY, bolaR + 1).fill({ color: cor, alpha: 0.7 });
  g.circle(0, bolaY, bolaR).fill({ color: 0xffffff, alpha: 1.0 });
}

function desenharSlotVazio(g: Graphics, cor: number, torreW: number): void {
  const slotR = torreW * 0.50;
  g.circle(0, 0, slotR).stroke({ color: cor, width: 1.3, alpha: 0.55 });
  g.circle(0, 0, slotR * 0.55).fill({ color: cor, alpha: 0.10 });
  const armLen = slotR * 0.55;
  g.moveTo(-armLen, 0).lineTo(armLen, 0)
    .stroke({ color: cor, width: 1.2, alpha: 0.55 });
  g.moveTo(0, -armLen).lineTo(0, armLen)
    .stroke({ color: cor, width: 1.2, alpha: 0.55 });
}
